import {
    createStyles,
    Text,
    Container,
    rem,
    Blockquote,
    Group,
    Radio,
    Input,
    Grid,
    Drawer,
    Title,
    Button, LoadingOverlay
} from '@mantine/core';
import {Sites} from "../sites/Sites";

import settings from "../settings.json";
import {IconAt, IconBellFilled, IconBrandInstagram, IconBrandWhatsapp} from "@tabler/icons-react";
import React, {useMemo} from "react";
import {useForm} from "@mantine/form";
import {useDisclosure, useMediaQuery} from "@mantine/hooks";
import Mailgun from "mailgun.js";
import {showNotification} from "@mantine/notifications";


const useStyles = createStyles((theme) => ({
    footer: {
        marginTop: rem(120),
        paddingTop: `calc(${theme.spacing.xl} * 2)`,
        paddingBottom: `calc(${theme.spacing.xl} * 2)`,
        backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.gray[0],
        borderTop: `${rem(1)} solid ${
            theme.colorScheme === 'dark' ? theme.colors.dark[5] : theme.colors.gray[2]
        }`,
    },

    logo: {
        maxWidth: rem(400),

        [theme.fn.smallerThan('sm')]: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
        },
    },

    description: {
        marginTop: rem(5),

        [theme.fn.smallerThan('sm')]: {
            marginTop: theme.spacing.xs,
            textAlign: 'center',
        },
    },

    inner: {
        display: 'flex',
        justifyContent: 'space-between',

        [theme.fn.smallerThan('sm')]: {
            flexDirection: 'column',
            alignItems: 'center',
        },
    },

    groups: {
        display: 'flex',
        flexWrap: 'wrap',

        [theme.fn.smallerThan('sm')]: {
            display: 'none',
        },
    },

    wrapper: {
        width: rem(160),
    },

    link: {
        display: 'block',
        color: theme.colorScheme === 'dark' ? theme.colors.dark[1] : theme.colors.gray[6],
        fontSize: theme.fontSizes.sm,
        paddingTop: rem(3),
        paddingBottom: rem(3),

        '&:hover': {
            textDecoration: 'underline',
        },
    },
    subscribe: {
        display: 'block',
        color: theme.colorScheme === 'dark' ? theme.colors.dark[1] : theme.colors.gray[6],
        fontSize: theme.fontSizes.sm,
        paddingTop: rem(3),
        paddingBottom: rem(3),
    },

    title: {
        fontSize: theme.fontSizes.lg,
        fontWeight: 700,
        fontFamily: `Greycliff CF, ${theme.fontFamily}`,
        marginBottom: `calc(${theme.spacing.xs} / 2)`,
        color: theme.colorScheme === 'dark' ? theme.white : theme.black,
    },

    blogTitle: {
        fontFamily: `Greycliff CF, ${theme.fontFamily}`,
        color: theme.colorScheme === 'dark' ? theme.white : theme.black,
    },

    afterFooter: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginTop: theme.spacing.xl,
        paddingTop: theme.spacing.xl,
        paddingBottom: theme.spacing.xl,
        borderTop: `${rem(1)} solid ${
            theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[2]
        }`,

        [theme.fn.smallerThan('sm')]: {
            flexDirection: 'column',
        },
    },

    social: {
        [theme.fn.smallerThan('sm')]: {
            marginTop: theme.spacing.xs,
        },
    },
}));

export default function MyFooter({ data }: FooterLinksProps) {
    const { classes } = useStyles();

    const [opened, { open, close }] = useDisclosure(false);
    const isMobile = useMediaQuery('(max-width: 800px)');

    const groups = data.map((group) => {
        const links = group.links.map((link, index) => (
            <Text<'a'>
                key={index}
                className={classes.link}
                component="a"
                href={link.link}
                onClick={(event) => {
                    event.preventDefault();
                    // @ts-ignore
                    if (Sites[link.link] === undefined){
                        window.open(link.link, '_blank')
                        return;
                    }
                    // eslint-disable-next-line no-restricted-globals
                    history.pushState(null, '', link.link)
                    window.dispatchEvent(new Event('popstate'));
                }}
            >
                {link.label}
            </Text>
        ));

        return (
            <div className={classes.wrapper} key={group.title}>
                <Text className={classes.title}>{group.title}</Text>
                {links}
            </div>
        );
    });

    return (
        <div>
            <Drawer
                w={isMobile? "100%": "md"}
                opened={opened}
                onClose={close}
                title={<Title order={3}>Benachrichtigungen einrichten</Title>}
                zIndex={10000}
                position={"left"}
                ml={-10}
            >
                <SubscribeSelectionContent close={close}/>
            </Drawer>
            <footer className={classes.footer}>

                <Container className={classes.inner}>
                    <div className={classes.logo}>
                        <h1 className={classes.blogTitle}>{settings.site_title}</h1>
                        <Text size="xs" color="dimmed" className={classes.description}>
                            {settings.site_description}
                        </Text>
                        <Group align={"end"}>
                            <IconBellFilled className={classes.subscribe}/>
                            <a href={"subscribe"} onClick={(event) => {
                                event.preventDefault();
                                open();
                            }} className={classes.subscribe}> Benachrichtigungen einrichten </a>
                        </Group>
                    </div>
                    <div className={classes.groups}>{groups}</div>
                </Container>
                <Container className={classes.afterFooter}>
                    <Blockquote cite={"- " + settings.footer_quote_author}>
                        <Text size="lg" color="dimmed">
                            {settings.footer_quote}
                        </Text>
                    </Blockquote>

                </Container>
            </footer>
        </div>
    );
}

interface FooterLinksProps {
    data: {
        title: string;
        links: { label: string; link: string }[];
    }[];
}

function SubscribeSelectionContent({close}: any){
    // @ts-ignore
    const subscribeSelectionForm = useForm({
        initialValues: {
            subscribeSelection: 'email',
            input: ''
        },
    })
    const [sendingEmail, setSendingEmail] = React.useState(false)

    const mgc = useMemo(() => {
        const formData = require('form-data');
        const mailgun = new Mailgun(formData);
        return mailgun.client({
            username: 'api',
            key: settings.mailgun.api_key,
            public_key: settings.mailgun.public_key,
        });
    }, [])


    return (
        <div>
            <form onSubmit={subscribeSelectionForm.onSubmit((values)=> {
                // send Email with Mailgun
                const data = {
                    from: "Anfrage für Newsletter des Blogs: <" + settings.mailgun.to + ">",
                    to: new Array(settings.mailgun.to),
                    subject: "Newsletter request via " + values.subscribeSelection,
                    text: "Anfrage für Newsletter-Informationen via " + values.subscribeSelection + " an: " + values.input,
                    html: "<p>Informationen über " + values.subscribeSelection + " an " + values.input +  "</p>",
                }
                setSendingEmail(true)
                try{
                    mgc.messages.create(settings.mailgun.domain, data).then(() => {
                        setSendingEmail(false)
                        close()
                        showNotification({
                            title: "Die Anfrage wurde gesendet",
                            message: "Du wirst in Zukunft über neue Einträge informiert",
                            color: "green",
                            autoClose: 5000,
                        })
                    });
                }catch (e){
                    setSendingEmail(false)
                    showNotification({
                        title: "Die Anfrage konnte nicht gesendet werden",
                        message: "Die Anfrage konnte nicht gesendet werden, bitte versuche es nochmal.",
                        color: "red",
                        autoClose: 5000,
                    })
                }
            })} >
                <LoadingOverlay visible={sendingEmail} overlayBlur={2} />
                <Grid>
                    <Grid.Col md={12}>
                        <Radio.Group
                            name="Wie soll ich dich benachrichtigen?"
                            label="Wie soll ich dich benachrichtigen?"
                            withAsterisk
                            {...subscribeSelectionForm.getInputProps('subscribeSelection')}
                        >
                            <Group position="center">
                                <Radio label="E-Mail" value="email"/>
                                <Radio label="WhatsApp" value="whatsapp"/>
                                <Radio label="Instagram" value="instagram"/>
                            </Group>
                        </Radio.Group>
                    </Grid.Col>
                    <Grid.Col md={12}>
                        <Input
                            required
                            placeholder={subscribeSelectionForm.values.subscribeSelection === 'email' ? 'E-Mail' : subscribeSelectionForm.values.subscribeSelection === 'whatsapp' ? 'Telefonnummer' : 'Instagram Benutzername'}
                            icon={subscribeSelectionForm.values.subscribeSelection === "email"? <IconAt/>: subscribeSelectionForm.values.subscribeSelection === "whatsapp"? <IconBrandWhatsapp />: <IconBrandInstagram/>}
                            {...subscribeSelectionForm.getInputProps('input')}
                        />
                    </Grid.Col>
                    <Grid.Col md={12}>
                        <Button.Group>
                            <Button
                                fullWidth
                                variant={"light"}
                                type={"submit"}
                            >
                                Bestätigen
                            </Button>
                            <Button
                                fullWidth
                                color={"red"}
                                variant={"light"}
                                onClick={()=>{
                                    close()
                                }}
                            >
                                Abbrechen
                            </Button>
                        </Button.Group>
                    </Grid.Col>
                </Grid>
            </form>
        </div>
    )
}
